import React, {useEffect, useMemo, useRef, useState} from 'react';
import moment from 'moment';
import {useNavigate, useParams} from "react-router-dom";
import API from "../../../utils/API";
import {API_ENDPOINTS} from "../../../apiConfig";
import Forms from "../../../components/Forms";
import DeleteIconButton from "../../../components/Forms/DeleteIconButton";
import EditIconButton from "../../../components/Forms/EditIconButton";
import {useModal} from "../../../contexts/ModalContext";
import SaleModal from "../../../components/SaleModal";
import SaleMarkSoldModal from "../../../components/SaleMarkSoldModal";
import routes from "../../../routesConfig";
import AdminDataView from "../../../components/AdminDataView";
import PhotoThumbWithHover from "../../../components/PhotoThumbWithHover";
import {resolveSalePhotoUrl} from "../../../utils/adminMedia";
import {getRowStatusClass} from "../../../utils/adminStatus";

const Sales = () => {
    const {id: routeSaleId} = useParams();
    const navigate = useNavigate();
    const [sales, setSales] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [filter, setFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const {openModal, confirm} = useModal();
    const openedFromRouteRef = useRef(null);

    const fetchSales = async () => {
        setLoading(true);
        try {
            const response = await API.get(API_ENDPOINTS.sales.list);
            const rows = (response.data || []).map((item) => ({...item, key: item._id}));
            setSales(rows);
            setFiltered(rows);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSales().catch(console.error);
    }, []);

    useEffect(() => {
        const q = String(filter || '').toLowerCase().trim();
        const rows = sales.filter((row) => {
            const haystack = [
                row.saleNumber,
                row.buyback?.buybackNumber,
                row.repair?.repairNumber,
                row.device?.name,
                row.device?.code,
                row.part?.name,
                row.buyer?.name,
                row.buyer?.surname,
                row.buyer?.email,
                row.buyer?.phone
            ].filter(Boolean).join(' ').toLowerCase();
            const statusOk = statusFilter ? row.status === statusFilter : true;
            return haystack.includes(q) && statusOk;
        });
        setFiltered(rows);
    }, [sales, filter, statusFilter]);

    const askDelete = async (row) => {
        const result = await confirm(`Opravdu chcete smazat prodej ${row.saleNumber || row._id}?`);
        if (result !== true) return;
        try {
            await API.delete(API_ENDPOINTS.sales.delete(row._id));
            setSales((prev) => prev.filter((item) => item._id !== row._id));
            setError(null);
        } catch (err) {
            setError(err);
        }
    };

    const openEdit = (row) => {
        openModal(SaleModal, {sale: row, onSaved: fetchSales}, {title: `Prodej ${row.saleNumber || ''}`});
    };
    const openMarkSold = (row) => {
        openModal(SaleMarkSoldModal, {sale: row, onSaved: fetchSales}, {title: `Označit jako prodáno`});
    };

    const columns = useMemo(() => ([
        {
            key: 'photo',
            name: 'Fotografie',
            renderCell: ({row}) => <PhotoThumbWithHover src={resolveSalePhotoUrl(row)} alt={row.saleNumber || 'Prodej'}/>
        },
        {
            key: 'saleNumber',
            name: 'Číslo prodeje',
            renderCell: ({row}) => row.saleNumber || '-'
        },
        {
            key: 'item',
            name: 'Položka',
            renderCell: ({row}) => {
                if (row.itemType === 'device') {
                    const deviceName = row.device?.name || '-';
                    const deviceCode = row.device?.code ? ` (${row.device.code})` : '';
                    return `${deviceName}${deviceCode}`;
                }
                return row.part?.name || '-';
            }
        },
        {
            key: 'source',
            name: 'Zdroj',
            renderCell: ({row}) => {
                if (row.sourceType === 'buyback') {
                    return `Výkup ${row.buyback?.buybackNumber || '-'}`;
                }
                if (row.sourceType === 'repair') {
                    return `Oprava ${row.repair?.repairNumber || '-'}`;
                }
                return '-';
            }
        },
        {
            key: 'salePrice',
            name: 'Prodejní cena',
            renderCell: ({row}) => row.salePrice === null || row.salePrice === undefined ? '-' : `${Number(row.salePrice).toLocaleString('cs-CZ')} CZK`
        },
        {
            key: 'purchasePrice',
            name: 'Nákupní cena',
            renderCell: ({row}) => row.purchasePrice === null || row.purchasePrice === undefined ? '-' : `${Number(row.purchasePrice).toLocaleString('cs-CZ')} CZK`
        },
        {
            key: 'repairCost',
            name: 'Cena oprav',
            renderCell: ({row}) => row.repairCost === null || row.repairCost === undefined ? '-' : `${Number(row.repairCost).toLocaleString('cs-CZ')} CZK`
        },
        {
            key: 'margin',
            name: 'Marže',
            renderCell: ({row}) => row.margin === null || row.margin === undefined ? '-' : `${Number(row.margin).toLocaleString('cs-CZ')} CZK`
        },
        {
            key: 'status',
            name: 'Stav',
            renderCell: ({row}) => row.status === 'sold' ? 'Prodáno' : 'Vystaveno k prodeji'
        },
        {
            key: 'buyer',
            name: 'Kupující',
            renderCell: ({row}) => [row.buyer?.name, row.buyer?.surname].filter(Boolean).join(' ') || row.buyer?.email || row.buyer?.phone || '-'
        },
        {
            key: 'saleDate',
            name: 'Datum prodeje',
            renderCell: ({row}) => row.saleDate ? moment(row.saleDate).format('DD.MM.YYYY') : '-'
        },
        {
            key: 'actions',
            name: 'Akce',
            renderCell: ({row}) => (
                <div className="flex items-center justify-end h-full w-full">
                    {row.status !== 'sold' ? (
                        <Forms.Button
                            className="!py-1 !px-2 !text-xs bg-emerald-600 hover:bg-emerald-700 mr-2"
                            label="Prodáno"
                            onClick={() => openMarkSold(row)}
                        />
                    ) : null}
                    <EditIconButton title="Upravit prodej" onClick={() => openEdit(row)}/>
                    <DeleteIconButton title="Smazat prodej" onClick={() => askDelete(row)}/>
                </div>
            )
        }
    ]).map((column) => ({...column, resizable: true})), []);

    useEffect(() => {
        if (!routeSaleId || !sales.length) return;
        if (openedFromRouteRef.current === routeSaleId) return;
        const row = sales.find((item) => item._id === routeSaleId);
        if (!row) return;
        openedFromRouteRef.current = routeSaleId;
        openModal(SaleModal, {
            sale: row,
            onSaved: fetchSales
        }, {
            title: `Prodej ${row.saleNumber || ''}`
        }).finally(() => {
            navigate(routes.admin.sales.itemsFull);
        });
    }, [routeSaleId, sales, openModal, navigate]);

    return (
        <div>
            <div className="grid grid-cols-3 gap-4 items-end mb-6">
                <Forms.Text
                    label="Hledat"
                    value={filter}
                    onChange={setFilter}
                    placeholder="Číslo, položka, kupující"
                />
                <Forms.Select
                    label="Stav"
                    value={[
                        {value: null, label: 'Vše'},
                        {value: 'listed', label: 'Vystaveno k prodeji'},
                        {value: 'sold', label: 'Prodáno'}
                    ].find((item) => item.value === statusFilter) || {value: null, label: 'Vše'}}
                    options={[
                        {value: null, label: 'Vše'},
                        {value: 'listed', label: 'Vystaveno k prodeji'},
                        {value: 'sold', label: 'Prodáno'}
                    ]}
                    onChange={(option) => setStatusFilter(option?.value || null)}
                />
                <Forms.Button
                    className="bg-gray-600 hover:bg-gray-700"
                    label="Reset"
                    onClick={() => {
                        setFilter('');
                        setStatusFilter(null);
                        fetchSales().catch(console.error);
                    }}
                />
            </div>
            {error ? <div className="text-sm text-red-600 mb-3">{error?.message || 'Nastala chyba.'}</div> : null}
            <AdminDataView
                section="sales"
                rows={filtered}
                columns={columns}
                loading={loading}
                resolvePhotoUrl={resolveSalePhotoUrl}
                dataGridProps={{rowClassRenderer: (row) => getRowStatusClass(row?.status)}}
            />
        </div>
    );
};

export default Sales;
